<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { ChevronRight, Trash2 } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Input } from '@/components/ui/input';

// ── Props ─────────────────────────────────────────────────────────────

const props = defineProps<NodeViewProps>();

// ── Title ─────────────────────────────────────────────────────────────

const title = ref<string>(props.node.attrs.title ?? 'Table des matières');
function onTitleBlur() {
    props.updateAttributes({ title: title.value });
}

// ── Live heading list ─────────────────────────────────────────────────

interface TocEntry {
    level: number;
    text: string;
    pos: number;
}

const entries = computed<TocEntry[]>(() => {
    if (!props.editor) {
        return [];
    }

    const items: TocEntry[] = [];
    props.editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading') {
            const level = node.attrs.level as number;
            const text = node.textContent;

            if (text.trim()) {
                items.push({ level, text, pos });
            }
        }

        return true;
    });

    return items;
});

// ── Navigation ────────────────────────────────────────────────────────

function jumpTo(pos: number) {
    if (!props.editor) {
        return;
    }

    props.editor.commands.focus(pos);
    const dom = props.editor.view.domAtPos(pos + 1).node as HTMLElement | null;

    if (dom && 'scrollIntoView' in dom) {
        (dom as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
}

// ── Indentation mapping ───────────────────────────────────────────────

const indentClass: Record<number, string> = {
    1: 'pl-0 font-semibold',
    2: 'pl-4',
    3: 'pl-8',
    4: 'pl-12 text-sm',
    5: 'pl-14 text-sm',
    6: 'pl-16 text-sm text-muted-foreground',
};
</script>

<template>
    <NodeViewWrapper
        as="div"
        class="toc-block my-6 rounded-lg border border-border/50 bg-muted/20 p-4"
    >
        <!-- Title + delete -->
        <div class="mb-3 flex items-center gap-2">
            <Input
                v-if="props.editor?.isEditable"
                v-model="title"
                class="flex-1 border-none bg-transparent p-0 text-sm font-semibold tracking-wide text-muted-foreground uppercase shadow-none focus-visible:ring-0"
                @blur="onTitleBlur"
            />
            <p
                v-else
                class="flex-1 text-sm font-semibold tracking-wide text-muted-foreground uppercase"
            >
                {{ title }}
            </p>
            <button
                v-if="props.editor?.isEditable"
                class="shrink-0 rounded p-1 text-muted-foreground/50 hover:bg-destructive/10 hover:text-destructive"
                title="Supprimer la table des matières"
                @click="props.deleteNode()"
            >
                <Trash2 class="h-3.5 w-3.5" />
            </button>
        </div>

        <!-- Entry list -->
        <nav v-if="entries.length > 0" class="space-y-0.5">
            <button
                v-for="(entry, i) in entries"
                :key="i"
                class="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                :class="indentClass[entry.level] ?? 'pl-0'"
                @click="jumpTo(entry.pos)"
            >
                <ChevronRight
                    v-if="entry.level === 1"
                    class="h-3 w-3 shrink-0 text-muted-foreground"
                />
                <span class="truncate">{{ entry.text }}</span>
            </button>
        </nav>

        <p v-else class="text-sm text-muted-foreground italic">
            Ajoutez des titres au document pour générer la table des matières.
        </p>
    </NodeViewWrapper>
</template>
